import React, { useState, useEffect, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import _ from "lodash";

import {
  Grid,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Slide,
  Box,
  Chip,
  Typography,
  Stack,
  Container,
} from "@mui/material";
import moment from "moment";

import CustomLayout from "views/CustomLayout";
import CustomBreadcrumbs from "components/Breadcrumbs";
import VerticalTabs from "components/VerticalTabs";
import Loader from "components/Loader";
import axiosInstance from "configs/axiosConfig";
import { APIRouteConstants } from "constants/routeConstants";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { updateCartCount } from "utils/website";
import { handleOpenAuthDialog } from "utils/auth";
import { GLOBAL_CURRENCY } from "constants/appConstants";
import {
  addCartItemsToLocalStorage,
  getCartItemsFromLocalStorage,
} from "utils/cart";
import { getAccessToken } from "utils";
import FlagContent from "components/InappropriateContent";
import { formatCurrencyNew } from "utils/helper";
import { updateWebsiteDetail } from "utils/website";
import StickyAddToCartBar from "./ProductSelectionBar";
import SizeChartDialog from "./SizeChartDialog ";

let formatCurrency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: GLOBAL_CURRENCY.code,
  currencyDisplay: "narrowSymbol",
  minimumFractionDigits: 2,
});

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const normalizeSizeLabel = (label = "") => {
  const s = String(label || "")
    .trim()
    .toLowerCase();
  if (s === "s" || s === "small") return "small";
  if (s === "m" || s === "medium") return "medium";
  if (s === "l" || s === "large") return "large";
  return s;
};

const capitalize = (str = "") =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

const getNextDate = (days) => {
  let date = moment();
  let next_date = date.add(days, "day");
  return moment(next_date).format(" ddd[,] D MMM");
};

const ProductView = (props) => {
  const [loader, setLoader] = useState(false);
  let { productId } = useParams();
  const [product, setProductDetail] = useState({});
  let { breadcrumbs } = props;
  let [imageList, setImageList] = useState([]);
  let [selectedImage, selectImage] = useState("");
  const [askLogin, openAskLogin] = useState(false);
  const loggedInUser = useSelector((state) => state.userDetails);

  // Variant selection state
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedColorKey, setSelectedColorKey] = useState(null); // color.id or color.name fallback
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(""); // store original label (e.g., "M", "s")
  const [availableSizes, setAvailableSizes] = useState([]); // all sizes labels from variants
  const [sizesByColor, setSizesByColor] = useState({}); // { colorKey: Set(sizeNorm) }
  const [availableColors, setAvailableColors] = useState([]); // [{ key, id?, name, hex_code }]

  // Inline validation errors (small red text)
  const [errorColor, setErrorColor] = useState("");
  const [errorSize, setErrorSize] = useState("");

  // Size guide dialog
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const openSizeGuide = () => setIsSizeGuideOpen(true);
  const closeSizeGuide = () => setIsSizeGuideOpen(false);

  const webDetails = useSelector((state) => state.webDetails);
  const { websiteInfo } = webDetails;
  const { user } = loggedInUser;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  var token = getAccessToken();

  const getProductDetail = async () => {
    setLoader(true);
    try {
      let res = await axiosInstance.get(
        APIRouteConstants.DASHBOARD.GET_PRODUCT(productId),
      );
      let { data } = res;
      if (!_.isEmpty(data)) {
        setProductDetail(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  const [isWebsiteFunctional, setIsWebsiteFunctional] = useState(false);
  const [hasCheckedWebsiteInfo, setHasCheckedWebsiteInfo] = useState(false); // 🔹 new flag

  const getWebsiteInfo = async () => {
    setLoader(true);
    try {
      const res = await axiosInstance.get(APIRouteConstants.DASHBOARD.WEB_INFO);
      const { data } = res;
      setIsWebsiteFunctional(Boolean(data?.status)); // or data?.data?.status
    } catch (err) {
      console.error(err);
      setIsWebsiteFunctional(false);
    } finally {
      setLoader(false);
      setHasCheckedWebsiteInfo(true);
    }
  };

  useEffect(() => {
    if (productId) getProductDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Build maps and initial selection from product.variants
  useEffect(() => {
    if (_.isEmpty(product)) return;

    if (product.variants && product.variants.length > 0) {
      const variants = product.variants;

      // Collect unique sizes (preserve original labels)
      const sizes = [
        ...new Set(variants.map((v) => v.size?.name).filter(Boolean)),
      ];
      setAvailableSizes(sizes);

      // Build color list and sizesByColor map. Use color.id if present; otherwise use color.name as key.
      const colorMap = new Map();
      const map = {};
      for (const v of variants) {
        const color = v.color || {};
        const colorKey =
          color.id || (color.name ? color.name.toLowerCase() : null);
        if (!colorKey) continue;
        if (!colorMap.has(colorKey)) {
          colorMap.set(colorKey, {
            key: colorKey,
            id: color.id,
            name: color.name,
            hex_code: color.hex_code,
          });
        }
        const sizeName = v.size?.name;
        if (!sizeName) continue;
        const norm = normalizeSizeLabel(sizeName);
        if (!map[colorKey]) map[colorKey] = new Set();
        map[colorKey].add(norm);
      }
      setSizesByColor(map);
      setAvailableColors(Array.from(colorMap.values()));

      // Initial selection: prefer first variant that has stock_count > 0; else first variant
      const preferred = variants.find((v) => v.stock_count > 0) || variants[0];
      if (preferred) {
        const prefColorKey =
          preferred.color?.id || preferred.color?.name?.toLowerCase();
        setSelectedVariant(preferred);
        // console.log("cartitem", preferred);
        setSelectedColorKey(prefColorKey);
        setSelectedSizeLabel(preferred.size?.name || "");
        const variantImages = preferred.images || [];
        setImageList(variantImages);
        console.log("imahes", variantImages[0]?.image_url);
        if (variantImages.length > 0)
          selectImage(variantImages[0].image || variantImages[0]?.image_url);
      }
    } else if (false) {
      // fallback for no variants: product-level images
      const { images, product_image } = product;
      const normalizedImages =
        Array.isArray(images) && images.length > 0
          ? images
          : product_image
            ? [{ image: product_image }]
            : [];
      setImageList(normalizedImages);
      if (normalizedImages.length > 0)
        selectImage(normalizedImages[0].image || normalizedImages[0]);
      setSelectedVariant(null);
      setSelectedColorKey(null);
      setSelectedSizeLabel("");
    }

    getWebsiteInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  // Helpers
  const findVariantBy = (sizeNorm, colorKey) => {
    if (!Array.isArray(product.variants)) return null;
    // match by normalized size + colorKey (color.id or color.name lowercased)
    let v = null;
    if (sizeNorm && colorKey) {
      v = product.variants.find(
        (x) =>
          normalizeSizeLabel(x.size?.name) === sizeNorm &&
          (x.color?.id === colorKey ||
            x.color?.name?.toLowerCase() === colorKey),
      );
      if (v) return v;
    }
    if (sizeNorm) {
      v = product.variants.find(
        (x) => normalizeSizeLabel(x.size?.name) === sizeNorm,
      );
      if (v) return v;
    }
    if (colorKey) {
      v = product.variants.find(
        (x) =>
          x.color?.id === colorKey || x.color?.name?.toLowerCase() === colorKey,
      );
      if (v) return v;
    }
    return product.variants[0] || null;
  };

  // When user picks a color (colorKey is id or lowercased name)
  const handleColorChange = (colorKey) => {
    setErrorColor("");
    setErrorSize("");

    // If already selected, no-op
    if (selectedColorKey === colorKey) return;

    // Prefer variant with this color and stock
    const byColor =
      product.variants.find(
        (v) =>
          (v.color?.id === colorKey ||
            v.color?.name?.toLowerCase() === colorKey) &&
          v.stock_count > 0,
      ) ||
      product.variants.find(
        (v) =>
          v.color?.id === colorKey || v.color?.name?.toLowerCase() === colorKey,
      );

    if (!byColor) return;

    // If previous size is available for this color, prefer it
    const prevSizeNorm = selectedSizeLabel
      ? normalizeSizeLabel(selectedSizeLabel)
      : null;
    const setForColor = sizesByColor[colorKey];
    let finalVariant = byColor;
    if (
      prevSizeNorm &&
      setForColor instanceof Set &&
      setForColor.has(prevSizeNorm)
    ) {
      const sameSizeVar = product.variants.find(
        (v) =>
          normalizeSizeLabel(v.size?.name) === prevSizeNorm &&
          (v.color?.id === colorKey ||
            v.color?.name?.toLowerCase() === colorKey),
      );
      if (sameSizeVar) finalVariant = sameSizeVar;
    }

    setSelectedVariant(finalVariant);
    setSelectedColorKey(colorKey);
    setSelectedSizeLabel(finalVariant.size?.name || "");
    const variantImages = finalVariant.images || [];
    setImageList(variantImages);
    if (variantImages.length > 0)
      selectImage(variantImages[0].image || variantImages[0]);
  };

  // When user picks a size (label is exact label from API)
  const handleSizeChange = (label) => {
    setErrorSize("");
    const sizeNorm = normalizeSizeLabel(label);
    // Prefer the same color if selected
    const preferred =
      findVariantBy(sizeNorm, selectedColorKey) ||
      findVariantBy(sizeNorm, undefined);
    if (!preferred) return;
    const prefColorKey =
      preferred.color?.id || preferred.color?.name?.toLowerCase();
    setSelectedVariant(preferred);
    setSelectedColorKey(prefColorKey);
    setSelectedSizeLabel(preferred.size?.name || "");
    const variantImages = preferred.images || [];
    setImageList(variantImages);
    if (variantImages.length > 0)
      selectImage(variantImages[0].image || variantImages[0]);
  };

  const addToCart = async () => {
    setLoader(true);

    // Inline validations
    // if (product.variants && product.variants.length > 0) {
    //   if (!selectedColorKey) {
    //     setErrorColor('Please select a color');
    //     setLoader(false);
    //     return;
    //   }
    //   if (!selectedSizeLabel) {
    //     setErrorSize('Please select a size');
    //     setLoader(false);
    //     return;
    //   }
    // }
    const productwithoutvarient = {
      category_name: product?.category_name,
      description: product?.description,
      name: product?.name,
    };

    const cartItem = { ...selectedVariant, ...productwithoutvarient };

    console.log("cartitem", cartItem);
    // return
    if (!token) {
      addCartItemsToLocalStorage(cartItem);
      updateCartCount(true);
      getProductDetail();
      navigate("/cart");
      enqueueSnackbar(
        "Hey, this feels great! Product has been added to the cart.",
        { variant: "success" },
      );
      setLoader(false);
      return;
    }

    try {
      let res = await axiosInstance.post(
        APIRouteConstants.DASHBOARD.ADD_TO_CART,
        {
          product_id: cartItem.id,
        },
      );
      let { data, response } = res;

      if (response && response.status === 400 && response.data) {
        if (response.data.error) {
          enqueueSnackbar(response.data.error, { variant: "error" });
        } else
          for (let i in response.data) {
            enqueueSnackbar(_.capitalize(i) + ": " + response.data[i][0], {
              variant: "error",
            });
          }
      }

      if (data) {
        navigate("/cart");
        enqueueSnackbar(
          "Hey, this feels great! Product has been added to the cart.",
          { variant: "success" },
        );
        updateCartCount(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };
  const handleAddToCart = () => {
    setErrorColor("");
    setErrorSize("");
    // No real variants (only one variant and no real options)
    if (
      product.variants?.length === 1 ||
      !product.variants[0].color ||
      !product.variants[0].size
    ) {
      addToCart();
      return;
    }

    const hasColorOption = product.variants?.some(
      (v) => v.color != null && v.color.name != "",
    );
    const hasSizeOption = product.variants?.some(
      (v) => v.size != null && v.size.name != "",
    );
    console.log("hasColorOption", hasColorOption);
    if (hasColorOption && !selectedColorKey) {
      enqueueSnackbar("Please select a color", { variant: "warning" });
      return;
    }

    if (hasSizeOption && !selectedSizeLabel) {
      enqueueSnackbar("Please select a size", { variant: "warning" });
      return;
    }

    addToCart();
  };

  const currentCart = getCartItemsFromLocalStorage() || [];
  const currentCartItem = currentCart.filter(
    (item) => item?.id === (selectedVariant?.id || product?.id),
  );

  const hasVariants = product.variants && product.variants.length > 0;
  const actualStock = hasVariants
    ? (selectedVariant?.stock_count ?? 0)
    : (product.count ?? 0);
  console.log("actualStock", actualStock);
  // Sizes to render for selected color (we hide unavailable sizes) — choice A
  const availableSizesForSelectedColor = () => {
    if (!selectedColorKey) {
      // If no color chosen, return sizes for the initial selectedVariant's color or all sizes
      if (selectedVariant) {
        const key =
          selectedVariant.color?.id ||
          selectedVariant.color?.name?.toLowerCase();
        const setForColor = sizesByColor[key];
        if (setForColor instanceof Set) {
          return availableSizes.filter((lbl) =>
            setForColor.has(normalizeSizeLabel(lbl)),
          );
        }
      }
      return availableSizes;
    }
    const setForColor = sizesByColor[selectedColorKey];
    if (!(setForColor instanceof Set)) return [];
    return availableSizes.filter((lbl) =>
      setForColor.has(normalizeSizeLabel(lbl)),
    );
  };
  const productImage =
    selectedVariant?.images?.[0]?.image ||
    selectedVariant?.images?.[0]?.image_url ||
    product?.images?.[0]?.image ||
    product?.product_image ||
    "";
  return (
    <CustomLayout>
      <Container maxWidth="lg" disableGutters>
        <Box
          className="content-container"
          sx={{
            maxWidth: "1250px",
            width: "100%",
            margin: "0 auto",
            px: { xs: 2, sm: 2, md: 3 },
            mt: {
              xs: "100px",
              md: "90px",
            },
          }}
        >
          <Loader open={loader} />
          <Box className="catalog-wrapper content-container">
            <CustomBreadcrumbs
              list={breadcrumbs}
              name={`${_.truncate(product?.category_name || "", { length: 40 })} / ${product?.name || ""}`}
            />
            {/* <Divider className="divider" /> */}
            <Grid
              container
              className="main-content-wrapper product-view-wrapper"
            >
              <Grid item lg={5} md={6} sm={12} xs={12}>
                <VerticalTabs
                  type="image"
                  itemList={imageList}
                  selectedItem={selectedImage}
                />
              </Grid>
              {/* <Grid
                item
                lg={7}
                md={6}
                sm={12}
                xs={12}
                className="description-wrapper"
              > */}
              <Grid
                item
                lg={6}
                md={6}
                sm={12}
                xs={12}
                sx={{
                  pl: { md: 5 },
                  mt: { xs: 4, md: 6, lg: 8 },
                }}
              >
                {/* <span className="title">{product?.name}</span> */}
                <Typography
                  sx={{
                    fontSize: "1.375rem",
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  {product?.name}
                </Typography>
                {/* Price display - show selected variant price or product price */}
                <Box className="price-wrapper" sx={{ mt: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "1.375rem",
                      fontWeight: 600,
                      lineHeight: 1,
                    }}
                  >
                    {formatCurrencyNew(
                      selectedVariant?.price || product?.price || 0,
                      websiteInfo?.currency_symbol,
                    )}
                  </Typography>
                </Box>

                {/* Variant Selection */}
                {product.variants && product.variants.length > 0 && (
                  <Box>
                    {/* Color Selection (swatch + name) */}
                    {availableColors.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            mt: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.95rem",
                              fontWeight: 400,
                              lineHeight: 1,

                              mr: 2,
                              whiteSpace: "nowrap",
                              color: "#040404",
                            }}
                          >
                            Color:{" "}
                            {capitalize(selectedVariant?.color?.name || "")}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          {availableColors.map((color) => {
                            const isSelected = selectedColorKey === color.key;
                            return (
                              <Box
                                key={color.key}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  mb: 1,
                                }}
                                onClick={() => handleColorChange(color.key)}
                              >
                                <Box
                                  sx={{
                                    width: 35,
                                    height: 35,

                                    backgroundColor: color.hex_code || "#eee",
                                    borderBottom: isSelected
                                      ? "3px solid rgba(0,0,0,0.12)"
                                      : "3px solid rgba(0,0,0,0.12)",
                                    boxSizing: "border-box",
                                  }}
                                  aria-hidden
                                />
                                {/* <Typography variant="caption" sx={{ mt: 0.6 }}>
                                    {color.name}
                                  </Typography> */}
                              </Box>
                            );
                          })}
                        </Stack>
                        {errorColor && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {errorColor}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Sizes - only show sizes available for the selected color */}
                    {availableSizes && availableSizes.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            mt: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.95rem",
                              fontWeight: 400,
                              lineHeight: 1,
                              mr: 2,
                              whiteSpace: "nowrap",
                              color: "#040404",
                            }}
                          >
                            Size:
                            {selectedSizeLabel?.toLowerCase() === "free size"
                              ? "Free Size"
                              : selectedSizeLabel?.toUpperCase()}
                            {/* {selectedSizeLabel?.toUpperCase()} */}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {availableSizesForSelectedColor().map((label) => {
                            const sizeNorm = normalizeSizeLabel(label);
                            const isActive =
                              selectedSizeLabel &&
                              normalizeSizeLabel(selectedSizeLabel) ===
                                sizeNorm;
                            return (
                              <Box
                                key={label}
                                size="small"
                                variant={isActive ? "contained" : "outlined"}
                                color={isActive ? "primary" : "inherit"}
                                onClick={() => handleSizeChange(label)}
                                sx={{
                                  minWidth: 35,
                                  height: 35,
                                  px: 1,
                                  textTransform: "capitalize",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  borderRadius: "0px", // 🔥 flat look
                                  backgroundColor: isActive
                                    ? "#000"
                                    : "#e9e9e9",
                                  color: isActive ? "#fff" : "#000",
                                  border: isActive
                                    ? "1px solid #000"
                                    : "1px solid #ddd",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: isActive
                                      ? "#000"
                                      : "#dcdcdc",
                                  },
                                }}
                              >
                                {label}
                              </Box>
                            );
                          })}
                        </Stack>

                        {errorSize && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {errorSize}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Stock Information */}
                    {/* {selectedVariant && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            fontWeight: 400,
                            lineHeight: 1,

                            mr: 2,
                            whiteSpace: "nowrap",
                            color: "#040404",
                          }}
                        >
                          Stock {selectedVariant.stock_count} available
                        </Typography>
                      </Box>
                    )} */}
                  </Box>
                )}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "var(--fs-xs)",
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                    color="text.primary"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "var(--fs-xs)",
                      fontWeight: 400,
                      lineHeight: 1,

                      mr: 2,
                      whiteSpace: "nowrap",
                    }}
                    color="text.primary"
                  >
                    Category: {product.category_name}
                  </Typography>
                </Box>
                {actualStock > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: "var(--fs-xs)",
                        fontWeight: 400,
                        lineHeight: 1,

                        mr: 2,
                        whiteSpace: "nowrap",
                        color: "#040404",
                      }}
                      color="text.primary"
                    >
                      Expected delivery by:
                      {getNextDate(product.order_processing_days)}
                    </Typography>
                  </Box>
                )}
                <br />
                {/* <br /> */}
                {/* <SizeChartDialog imageSrc="/images/sizechart.png" /> */}

                {/* Stock status */}
                {(() => {
                  const stockCount = actualStock;
                  if (stockCount === 0) {
                    return (
                      <span
                        style={{ color: "maroon" }}
                        className="inventory-text"
                      >
                        Out of Stock
                      </span>
                    );
                  } else if (stockCount < 6) {
                    return (
                      <span className="inventory-text">
                        Only {stockCount} item(s) left
                      </span>
                    );
                  }
                  return "";
                })()}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 3,
                  }}
                >
                  {/* Add To Cart */}
                  <Button
                    fullWidth
                    onClick={() => handleAddToCart()}
                    disabled={
                      !isWebsiteFunctional ||
                      actualStock <= (currentCartItem[0]?.quantity || 0)
                    }
                    sx={{
                      height: 50,
                      borderRadius: "0.1rem",
                      background: "#000",
                      color: "#fff",
                      fontSize: {
                        xs: "0.8rem", // ⭐ little bigger
                        sm: "0.9rem",
                        md: "1rem",
                      },
                      fontWeight: 500,
                      textTransform: "none",
                      display: "flex",
                      gap: { xs: 0.2, md: 1 },
                      whiteSpace: "nowrap",
                      justifyContent: "center",
                      "&:hover": {
                        background: "#222",
                      },
                      "&.Mui-disabled": {
                        background: "#e0e0e0",
                        color: "#9e9e9e",
                      },
                    }}
                  >
                    Add to cart
                    <ShoppingCartOutlinedIcon fontSize="small" />
                  </Button>

                  {/* Buy Now CTA */}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => handleAddToCart()}
                    disabled={
                      !isWebsiteFunctional ||
                      actualStock <= (currentCartItem[0]?.quantity || 0)
                    }
                    sx={{
                      height: 50,
                      borderRadius: "0.1rem",
                      borderColor: "#000",
                      color: "#000",
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.9rem",
                        md: "1rem",
                      },
                      fontWeight: 500,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#000",
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#e0e0e0",
                        color: "#9e9e9e",
                      },
                    }}
                  >
                    Buy Now
                  </Button>

                  {/* Flag Icon */}
                  <FlagContent
                    product={{
                      ...product,
                      product_image:
                        selectedVariant?.images?.[0]?.image ||
                        selectedVariant?.images?.[0]?.image_url ||
                        product?.images?.[0]?.image ||
                        product?.product_image ||
                        "",
                    }}
                    productName={product?.name}
                  />
                </Box>
                {hasCheckedWebsiteInfo && !isWebsiteFunctional && (
                  <div
                    style={{
                      marginTop: "12px",
                      backgroundColor: "#FFEBEE",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #C62828",
                      color: "#C62828",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "0.95rem",
                    }}
                  >
                    <span>⚠️</span>
                    The store is currently offline. Please try again later.
                  </div>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
      {/* Size Guide Dialog (unchanged) */}
      <Dialog
        open={isSizeGuideOpen}
        onClose={closeSizeGuide}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Size Guide</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Tops (Apparel Upper)
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px" }}>Size</th>
                  <th style={{ textAlign: "left", padding: "6px" }}>US</th>
                  <th style={{ textAlign: "left", padding: "6px" }}>EU</th>
                  <th style={{ textAlign: "left", padding: "6px" }}>
                    Chest (cm)
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["XS", "34", "44", "84-89"],
                  ["S", "36", "46", "90-95"],
                  ["M", "38", "48", "96-101"],
                  ["L", "40", "50", "102-107"],
                  ["XL", "42", "52", "108-113"],
                  ["XXL", "44", "54", "114-119"],
                ].map((r) => (
                  <tr key={r[0]}>
                    {r.map((c, i) => (
                      <td
                        key={i}
                        style={{
                          padding: "6px",
                          borderTop: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Bottoms (Apparel Lower)
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px" }}>
                    Waist (W)
                  </th>
                  <th style={{ textAlign: "left", padding: "6px" }}>
                    Length (L)
                  </th>
                  <th style={{ textAlign: "left", padding: "6px" }}>
                    Waist (cm)
                  </th>
                  <th style={{ textAlign: "left", padding: "6px" }}>
                    Inseam (cm)
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["28", "30", "71-74", "76"],
                  ["30", "32", "76-79", "81"],
                  ["32", "32", "81-84", "81"],
                  ["34", "32", "86-89", "81"],
                  ["36", "32", "91-94", "81"],
                ].map((r, idx) => (
                  <tr key={idx}>
                    {r.map((c, i) => (
                      <td
                        key={i}
                        style={{
                          padding: "6px",
                          borderTop: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Shoes
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "6px" }}>UK</th>
                  <th style={{ textAlign: "left", padding: "6px" }}>US</th>
                  <th style={{ textAlign: "left", padding: "6px" }}>EU</th>
                  <th style={{ textAlign: "left", padding: "6px" }}>
                    Foot length (cm)
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["6", "7", "40", "25.4"],
                  ["7", "8", "41", "26.2"],
                  ["8", "9", "42", "27.1"],
                  ["9", "10", "43", "27.9"],
                  ["10", "11", "44", "28.8"],
                ].map((r, idx) => (
                  <tr key={idx}>
                    {r.map((c, i) => (
                      <td
                        key={i}
                        style={{
                          padding: "6px",
                          borderTop: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSizeGuide}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* {(product?.variants?.length !== 1 || !product?.variants?.[0]?.color===null || !product?.variants?.[0]?.size===null) ? 
      ( */}
      <StickyAddToCartBar
        product={product && product}
        selectedVariant={selectedVariant}
        availableSizes={availableSizes}
        availableColors={availableColors}
        selectedSizeLabel={selectedSizeLabel}
        selectedColorKey={selectedColorKey}
        handleSizeChange={handleSizeChange}
        handleColorChange={handleColorChange}
        handleAddToCart={handleAddToCart}
        errorSize={errorSize}
        availableSizesForSelectedColor={availableSizesForSelectedColor}
        errorColor={errorColor}
        isAddToCartVisible="add-to-cart-btn"
        websiteInfo={websiteInfo}
        currentCartItem={currentCartItem}
      />
      {/* ):<></> } */}
    </CustomLayout>
  );
};

export default ProductView;
